import { useState, useEffect, useCallback } from 'react';
import { fetchWithInterceptors } from '../utils/apiInterceptors';

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '';
import { useAuth } from './useAuth';
import { redirectToCheckout, redirectToBillingPortal } from '../utils/stripe';

export interface BillingPlan {
  id: string;
  name: string;
  displayName: string;
  stripePriceIdMonthly: string | null;
  stripePriceIdYearly: string | null;
  priceMonthly: number | null;
  priceYearly?: number | null;
  maxQuotesMonthly: number;
  maxAgents: number;
  trialDays: number;
  features: string[];
  highlights?: string[];
  badge?: string | null;
  contact_only?: boolean;
  contact_email?: string | null;
  contact_cta?: string | null;
  trial_quote_limit?: number | null;
  retention_days?: number;
  draft_retention_days?: number;
  quote_retention_days?: number;
  position: number;
  active: boolean;
}

interface Subscription {
  id: string;
  status: string;
  trial_start?: string;
  trial_end?: string;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  created_at: string;
  grace_period_ends?: string | null;
  next_payment_date?: string | null;
  last_payment_date?: string | null;
}

interface Usage {
  quotes_used_current_period: number;
  quotes_used_total: number;
  agents_count: number;
  period_start: string;
  period_end: string;
  overage_quotes: number;
}

export interface QuotaSummary {
  used: number;
  remaining: number;
  limit: number;
  unlimited?: boolean;
  trial_limit?: number | null;
}

type RawUsage =
  | number
  | null
  | undefined
  | {
      quotes_used_current_period?: number | null;
      quotes_used?: number | null;
      current?: number | null;
      count?: number | null;
      usage_count?: number | null;
    };

type RawBillingPayload = {
  quota?: Partial<QuotaSummary> & { unlimited?: boolean } | null;
  usage?: RawUsage;
  usage_count?: number | null;
  limit?: number | null;
  remaining?: number | null;
  plan?: {
    monthly_quote_limit?: number | null;
    max_quotes_per_month?: number | null;
  } | null;
  subscription?: {
    status?: string | null;
  } | null;
  trial_info?: {
    quote_limit?: number | null;
    quotes_remaining?: number | null;
    limit?: number | null;
  } | null;
};

const TRIAL_STATUS_TOKENS = ['trial', 'trial-period'];

function coerceNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return undefined;
}

function pickNumber(...values: unknown[]): number | undefined {
  for (const candidate of values) {
    const coerced = coerceNumber(candidate);
    if (coerced !== undefined) {
      return coerced;
    }
  }
  return undefined;
}

function isTrialStatus(status: string | null | undefined): boolean {
  if (!status) {
    return false;
  }
  const normalized = status.toLowerCase();
  return TRIAL_STATUS_TOKENS.some(token => normalized.includes(token));
}

export function normalizeQuotaSummary(payload: RawBillingPayload | null | undefined): QuotaSummary | undefined {
  if (!payload) {
    return undefined;
  }

  const rawQuota = payload.quota || undefined;
  const trialInfo = payload.trial_info || undefined;
  const isTrial = isTrialStatus(payload.subscription?.status);

  const usageFromQuota = pickNumber(rawQuota?.used);

  let usageFromUsageField: number | undefined;
  const rawUsage = payload.usage;
  if (typeof rawUsage === 'number' || typeof rawUsage === 'string') {
    usageFromUsageField = coerceNumber(rawUsage);
  } else if (rawUsage && typeof rawUsage === 'object') {
    usageFromUsageField = pickNumber(
      rawUsage.quotes_used_current_period,
      rawUsage.quotes_used,
      rawUsage.current,
      rawUsage.count,
      rawUsage.usage_count
    );
  }

  const usage = pickNumber(
    usageFromQuota,
    payload.usage_count,
    usageFromUsageField
  ) ?? 0;

  const trialLimit = pickNumber(rawQuota?.trial_limit, trialInfo?.quote_limit, trialInfo?.limit);

  const limitCandidates: Array<number | undefined> = [
    pickNumber(rawQuota?.limit),
    isTrial ? trialLimit : undefined,
    pickNumber(payload.limit),
    trialLimit,
    pickNumber(payload.plan?.monthly_quote_limit),
    pickNumber(payload.plan?.max_quotes_per_month)
  ];

  const limit = (limitCandidates.find(value => value !== undefined) ?? 0) as number;
  const unlimited = rawQuota?.unlimited === true || limit === -1;

  const rawRemaining = pickNumber(rawQuota?.remaining, payload.remaining, trialInfo?.quotes_remaining);

  let remaining: number;
  if (unlimited) {
    remaining = -1;
  } else if (rawRemaining !== undefined) {
    remaining = Math.max(0, rawRemaining);
  } else if (limit > 0) {
    remaining = Math.max(0, limit - usage);
  } else {
    remaining = 0;
  }

  const hasMeaningfulData = unlimited || limit > 0 || usage > 0;
  if (!hasMeaningfulData) {
    return undefined;
  }

  return {
    used: usage,
    remaining,
    limit,
    unlimited,
    trial_limit: trialLimit ?? (isTrial && limit > 0 ? limit : null)
  };
}

export interface BillingStatus {
  has_subscription: boolean;
  plan: BillingPlan | null;
  subscription: Subscription | null;
  usage: Usage | null;
  // Legacy computed fields for backward compatibility
  usage_count?: number;
  limit?: number;
  remaining?: number;
  trial_info?: {
    active: boolean;
    days_remaining: number;
    expires_on: string;
    expired?: boolean;
  } | null;
  quota?: QuotaSummary;
  is_trial?: boolean;
}

export function useBilling() {
  const { user } = useAuth();
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBillingStatus = async () => {
    if (!user?.companyId) {
      setLoading(false);
      return;
    }

  try {
    const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/subscription-status`, {
      headers: {
        'Content-Type': 'application/json'
      }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        const responseData = data.data as RawBillingPayload & Record<string, unknown>;

        const subscriptionStatus = responseData.subscription?.status || null;
        const trialActive = Boolean(responseData.trial_info?.active) || isTrialStatus(subscriptionStatus);
        const hasSubscription = Boolean(responseData.has_subscription ?? responseData.subscription);

        const quota = normalizeQuotaSummary(responseData);
        const usage_count = quota?.used ?? 0;
        const limit = quota?.limit ?? responseData.plan?.monthly_quote_limit ?? 0;
        const remaining = quota?.unlimited ? -1 : quota?.remaining ?? Math.max(0, limit - usage_count);

        let trial_info = responseData.trial_info ?? null;
        if (!trial_info && isTrialStatus(responseData.subscription?.status)) {
          const trialEndRaw = responseData.subscription?.trial_end
            || (responseData.subscription as Record<string, unknown>)?.trial_expires_at
            || (responseData.subscription as Record<string, unknown>)?.trial_expires
            || (responseData.subscription as Record<string, unknown>)?.trial_ends_at
            || (responseData.subscription as Record<string, unknown>)?.trial_expiration
            || null;

          if (trialEndRaw) {
            const trialEnd = new Date(trialEndRaw);
            const now = new Date();
            const daysRemaining = Math.max(0, Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
            const expired = trialEnd < now;

            trial_info = {
              active: !expired,
              days_remaining: daysRemaining,
              expires_on: trialEnd.toISOString(),
              expired
            };
          }
        }

        const billingStatus: BillingStatus = {
          ...responseData,
          has_subscription: hasSubscription,
          is_trial: trialActive,
          usage_count,
          limit,
          remaining,
          trial_info,
          quota
        };

        setBillingStatus(billingStatus);
        setError(null);
      } else {
        throw new Error(data.error?.message || 'Failed to fetch billing status');
      }
    } catch (err) {
      console.error('Failed to fetch billing status:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setBillingStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillingStatus();
  }, [user?.companyId]);

  const refreshBillingStatus = () => {
    setLoading(true);
    setError(null);
    fetchBillingStatus();
  };

  const createCheckoutSession = useCallback(async ({
    priceId,
    planSlug,
    billingPeriod = 'monthly',
    successUrl,
    cancelUrl
  }: {
    priceId: string | null | undefined;
    planSlug?: string;
    billingPeriod?: 'monthly' | 'yearly';
    successUrl?: string;
    cancelUrl?: string;
  }) => {
    try {
      if (!priceId) {
        throw new Error('Missing Stripe price for the selected plan');
      }

      const resolvedSuccessUrl = successUrl ?? `${window.location.origin}/org/settings/billing?success=true`;
      const resolvedCancelUrl = cancelUrl ?? `${window.location.origin}/org/settings/billing?canceled=true`;

      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          priceId,
          planSlug,
          planId: planSlug,
          billingPeriod,
          successUrl: resolvedSuccessUrl,
          cancelUrl: resolvedCancelUrl
        })
      });

      if (!response.ok) {
        let message = 'Failed to create checkout session';
        try {
          const error = await response.json();
          message = error.error?.message || message;
        } catch (_) {
          // ignore JSON parse errors – fallback message already set
        }
        throw new Error(message);
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
        return;
      }

      // Redirect to Stripe Checkout via session id (fallback for existing implementation)
      if (data.sessionId) {
        await redirectToCheckout(data.sessionId);
        return;
      }

      throw new Error('Checkout session created without redirect information');
    } catch (err) {
      console.error('Failed to create checkout session:', err);
      throw err;
    }
  }, []);

  const createBillingPortalSession = useCallback(async () => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/portal/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          returnUrl: `${window.location.origin}/org/settings/billing`
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to create billing portal session');
      }

      const data = await response.json();

      // Redirect to Stripe Billing Portal
      if (data.success && data.data?.portal_url) {
        redirectToBillingPortal(data.data.portal_url);
      } else {
        throw new Error(data.error?.message || 'Failed to get portal URL');
      }
    } catch (err) {
      console.error('Failed to create billing portal session:', err);
      throw err;
    }
  }, []);

  const updateSubscription = useCallback(async ({
    newPriceId,
    planSlug,
    billingPeriod,
    prorationBehavior = 'create_prorations'
  }: {
    newPriceId: string | null | undefined;
    planSlug?: string;
    billingPeriod?: 'monthly' | 'yearly';
    prorationBehavior?: 'create_prorations' | 'none' | 'always_invoice' | 'default';
  }) => {
    try {
      if (!newPriceId) {
        throw new Error('Missing Stripe price for the selected plan');
      }

      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/update-subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newPriceId,
          prorationBehavior,
          planSlug,
          billingPeriod
        })
      });

      if (!response.ok) {
        let message = 'Failed to update subscription';
        try {
          const error = await response.json();
          message = error.error?.message || message;
        } catch (_) {
          // ignore JSON parse errors; keep fallback message
        }
        throw new Error(message);
      }

      await fetchBillingStatus();

      return await response.json();
    } catch (err) {
      console.error('Failed to update subscription:', err);
      throw err;
    }
  }, [fetchBillingStatus]);

  const cancelSubscription = useCallback(async (immediately = false, reason?: string) => {
    try {
      const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/cancel-subscription`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          immediately,
          reason
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Failed to cancel subscription');
      }

      const data = await response.json();
      
      // Refresh billing status after cancellation
      await fetchBillingStatus();
      
      return data;
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      throw err;
    }
  }, []);

  return {
    billingStatus,
    loading,
    error,
    refreshBillingStatus,
    createCheckoutSession,
    createBillingPortalSession,
    updateSubscription,
    cancelSubscription
  };
}

export function useBillingPlans() {
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [trialMeta, setTrialMeta] = useState<{ days: number; quote_limit: number } | null>(null);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await fetchWithInterceptors(`${API_BASE_URL}/api/v1/billing/plans`);
        const data = await response.json();
        
        if (data.success) {
          setPlans(data.data.plans);
          if (data.data.trial) {
            setTrialMeta(data.data.trial);
          }
          setError(null);
        } else {
          throw new Error(data.error?.message || 'Failed to fetch billing plans');
        }
      } catch (err) {
        console.error('Failed to fetch billing plans:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  return { plans, loading, error, trialMeta };
}
